import "@/App.css";
import "@/styles/platform.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Landing from "@/components/Landing";
import { AuthProvider } from "@/lib/auth";
import PrivateRoute from "@/components/PrivateRoute";
import ScrollToTop from "@/components/ScrollToTop";
import Login from "@/pages/Login";
import Settings from "@/pages/Settings";
import { PaymentSuccess, PaymentCancelled } from "@/pages/Payment";
import SetPassword from "@/pages/SetPassword";
import ResetPassword from "@/pages/ResetPassword";
import Empieza from "@/pages/Empieza";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminLeadDetail from "@/pages/admin/AdminLeadDetail";
import AdminOnboardings from "@/pages/admin/AdminOnboardings";
import AdminOnboardingDetail from "@/pages/admin/AdminOnboardingDetail";
import OnboardingLink from "@/pages/OnboardingLink";
import OnboardingForm from "@/pages/OnboardingForm";
import AdminPlanReviews from "@/pages/admin/AdminPlanReviews";
import AdminPlanReviewDetail from "@/pages/admin/AdminPlanReviewDetail";
import MyPlanning from "@/pages/client/MyPlanning";
import MyNutritionInfo from "@/pages/client/MyNutritionInfo";
import AdminMethodology from "@/pages/admin/AdminMethodology";
import AdminNutrition from "@/pages/admin/AdminNutrition";
import AdminServiceAlerts from "@/pages/admin/AdminServiceAlerts";
import MyArea from "@/pages/client/MyArea";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminClients from "@/pages/admin/AdminClients";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import AdminTemplates from "@/pages/admin/AdminTemplates";
import AdminPrograms from "@/pages/admin/AdminPrograms";
import AdminProgramEditor from "@/pages/admin/AdminProgramEditor";
import AdminDiagnostics from "@/pages/admin/AdminDiagnostics";
import AdminDiagnosticDetail from "@/pages/admin/AdminDiagnosticDetail";
import AdminExerciseCatalog from "@/pages/admin/AdminExerciseCatalog";
import AdminPlansV2 from "@/pages/admin/AdminPlansV2";
import AdminPlanV2Editor from "@/pages/admin/AdminPlanV2Editor";
import AdminPlanTemplates from "@/pages/admin/AdminPlanTemplates";
import AdminClientManagement from "@/pages/admin/AdminClientManagement";
import AdminAttention from "@/pages/admin/AdminAttention";
import AdminMatches from "@/pages/admin/AdminMatches";
import AdminTestsV2 from "@/pages/admin/AdminTestsV2";
import AdminTestLibrary from "@/pages/admin/AdminTestLibrary";
import PublicDiagnostic from "@/pages/PublicDiagnostic";
import ClientDashboard from "@/pages/client/ClientDashboard";
import ClientDashboardV2 from "@/pages/client/ClientDashboardV2";
import ClientReadiness from "@/pages/client/ClientReadiness";
import ClientTraining from "@/pages/client/ClientTraining";
import ClientNutrition from "@/pages/client/ClientNutrition";
import ClientTests from "@/pages/client/ClientTests";
import ClientWellness from "@/pages/client/ClientWellness";
import AdminWellness from "@/pages/admin/AdminWellness";
import AdminAutoreg from "@/pages/admin/AdminAutoreg";
import ClientFeedback from "@/pages/client/ClientFeedback";
import ClientPrograms from "@/pages/client/ClientPrograms";
import ClientProgramView from "@/pages/client/ClientProgramView";
import ProgramsCatalog from "@/pages/programs/ProgramsCatalog";
import ProgramDetail from "@/pages/programs/ProgramDetail";
import ProgramThanks from "@/pages/programs/ProgramThanks";

function App() {
  return (
    <div className="App">
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          style: { background: "#0c0c0c", color: "#fff", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 0 },
        }}
      />
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />

            {/* Public Diagnostic (token-scope, no auth) */}
            <Route path="/diagnostico/:token" element={<PublicDiagnostic />} />

            {/* Public Programs */}
            <Route path="/programas" element={<ProgramsCatalog />} />
            <Route path="/programas/gracias" element={<ProgramThanks />} />
            <Route path="/programas/:slug" element={<ProgramDetail />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/empieza" element={<Empieza />} />
            <Route path="/app/admin/solicitudes" element={<Navigate to="/app/admin/leads" replace />} />
            <Route path="/app/admin/leads" element={<PrivateRoute adminOnly><AdminLeads /></PrivateRoute>} />
            <Route path="/incorporacion/:token" element={<OnboardingLink />} />
            <Route path="/app/incorporacion" element={<PrivateRoute allowProspect><OnboardingForm /></PrivateRoute>} />
            <Route path="/app/admin/planificaciones" element={<PrivateRoute adminOnly><AdminPlanReviews /></PrivateRoute>} />
            <Route path="/app/admin/planificaciones/:id" element={<PrivateRoute adminOnly><AdminPlanReviewDetail /></PrivateRoute>} />
            <Route path="/app/admin/metodologia" element={<PrivateRoute adminOnly><AdminMethodology /></PrivateRoute>} />
            <Route path="/app/admin/nutricion" element={<PrivateRoute adminOnly><AdminNutrition /></PrivateRoute>} />
            <Route path="/app/admin/vencimientos" element={<PrivateRoute adminOnly><AdminServiceAlerts /></PrivateRoute>} />
            <Route path="/app/mi-area" element={<PrivateRoute><MyArea /></PrivateRoute>} />
            <Route path="/app/mi-planificacion" element={<PrivateRoute><MyPlanning /></PrivateRoute>} />
            <Route path="/app/nutricion-datos" element={<PrivateRoute><MyNutritionInfo /></PrivateRoute>} />
            <Route path="/app/admin/incorporaciones" element={<PrivateRoute adminOnly><AdminOnboardings /></PrivateRoute>} />
            <Route path="/app/admin/incorporaciones/:id" element={<PrivateRoute adminOnly><AdminOnboardingDetail /></PrivateRoute>} />
            <Route path="/app/admin/leads/:id" element={<PrivateRoute adminOnly><AdminLeadDetail /></PrivateRoute>} />

            {/* Platform */}
            <Route path="/app/login" element={<Login />} />
            <Route path="/app/pago-exitoso" element={<PaymentSuccess />} />
            <Route path="/app/pago-cancelado" element={<PaymentCancelled />} />

            {/* Admin */}
            <Route path="/app/admin" element={<PrivateRoute adminOnly><AdminDashboard /></PrivateRoute>} />
            <Route path="/app/admin/clients" element={<PrivateRoute adminOnly><AdminClients /></PrivateRoute>} />
            <Route path="/app/admin/clients/:id" element={<PrivateRoute adminOnly><AdminClientDetail /></PrivateRoute>} />
            <Route path="/app/admin/feedback" element={<PrivateRoute adminOnly><AdminFeedback /></PrivateRoute>} />
            <Route path="/app/admin/plantillas" element={<PrivateRoute adminOnly><AdminTemplates /></PrivateRoute>} />
            <Route path="/app/admin/programas" element={<PrivateRoute adminOnly><AdminPrograms /></PrivateRoute>} />
            <Route path="/app/admin/programas/:id" element={<PrivateRoute adminOnly><AdminProgramEditor /></PrivateRoute>} />
            <Route path="/app/admin/diagnosticos" element={<PrivateRoute adminOnly><AdminDiagnostics /></PrivateRoute>} />
            <Route path="/app/admin/diagnosticos/:id" element={<PrivateRoute adminOnly><AdminDiagnosticDetail /></PrivateRoute>} />
            <Route path="/app/admin/ejercicios" element={<PrivateRoute adminOnly><AdminExerciseCatalog /></PrivateRoute>} />
            <Route path="/app/admin/plans-v2" element={<PrivateRoute adminOnly><AdminPlansV2 /></PrivateRoute>} />
            <Route path="/app/admin/plans-v2/:id" element={<PrivateRoute adminOnly><AdminPlanV2Editor /></PrivateRoute>} />
            <Route path="/app/admin/plantillas-v2" element={<PrivateRoute adminOnly><AdminPlanTemplates /></PrivateRoute>} />
            <Route path="/app/admin/gestion" element={<PrivateRoute adminOnly><AdminClientManagement /></PrivateRoute>} />
            <Route path="/app/admin/atencion" element={<PrivateRoute adminOnly><AdminAttention /></PrivateRoute>} />
            <Route path="/app/admin/partidos" element={<PrivateRoute adminOnly><AdminMatches /></PrivateRoute>} />
            <Route path="/app/admin/tests-v2" element={<PrivateRoute adminOnly><AdminTestsV2 /></PrivateRoute>} />
            <Route path="/app/admin/biblioteca-tests" element={<PrivateRoute adminOnly><AdminTestLibrary /></PrivateRoute>} />

            {/* Client */}
            <Route path="/app/dashboard" element={<PrivateRoute><ClientDashboard /></PrivateRoute>} />
            <Route path="/app/mi" element={<PrivateRoute><ClientDashboardV2 /></PrivateRoute>} />
            <Route path="/app/readiness" element={<PrivateRoute><ClientReadiness /></PrivateRoute>} />
            <Route path="/app/entrenamiento" element={<PrivateRoute><ClientTraining /></PrivateRoute>} />
            <Route path="/app/nutricion" element={<PrivateRoute><ClientNutrition /></PrivateRoute>} />
            <Route path="/app/tests" element={<PrivateRoute><ClientTests /></PrivateRoute>} />
            <Route path="/app/bienestar" element={<PrivateRoute><ClientWellness /></PrivateRoute>} />
            <Route path="/app/admin/bienestar" element={<PrivateRoute adminOnly><AdminWellness /></PrivateRoute>} />
            <Route path="/app/admin/autorregulacion" element={<PrivateRoute adminOnly><AdminAutoreg /></PrivateRoute>} />
            <Route path="/app/feedback" element={<PrivateRoute><ClientFeedback /></PrivateRoute>} />
            <Route path="/app/programas" element={<PrivateRoute><ClientPrograms /></PrivateRoute>} />
            <Route path="/app/programas/:id" element={<PrivateRoute><ClientProgramView /></PrivateRoute>} />

            {/* Shared */}
            <Route path="/app/ajustes" element={<PrivateRoute><Settings /></PrivateRoute>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
